import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { TierNameValue } from "@ugg/shared/utils/tier-helpers";

// ─────────────────────────────────────────────────────────────────────
// ARAM Mayhem shared data layer
// ─────────────────────────────────────────────────────────────────────
//
// Endpoint reference: docs/lol-custom-data-feeds.md (§4). Update it when
// you add, move, or retire a feed here.
//
// Types, cleaners, and JSONFetcher hooks for the ARAM Mayhem custom
// data pipeline. Lives in the shared package so both the tier-list
// surfaces (AramMayhemTierListContainer + friends, also under
// @ugg/shared) and other consumers (e.g. AramMayhemSEO on the champion
// profile page) can use the same fetch layer — same URLs, same cache
// slots, so useJSONFetcher dedupes across renderers on the same page
// with no additional network cost.
//
// This module is deliberately data-only: no UI, no styling constants,
// no React components. FE surfaces that need presentation-layer
// constants (tier color maps, badge classes, etc.) define those next to
// their components; only the raw shape definitions live here.
//
// PATCH CONVENTION — two tiers, matching hooks/classic.ts:
//   • Route-bound hooks (the three tier-list feeds) resolve `patch`
//     themselves via useValidateQueryParams against a fixed
//     SPECIAL_TIER_LIST_PAGES id. They only work on their own route.
//   • Everything else takes an explicit named `patch` option, because
//     it's called from the champion build page and the SEO block too,
//     where there is no tier-list page id to validate against. The
//     caller resolves the patch and passes it in.
// Don't add a third style that reads `params.patch` without validating
// — it looks identical at the callsite but fails differently.

// ─────────────────────────────────────────────────────────────────────
// CDN roots
// ─────────────────────────────────────────────────────────────────────

// Tier lists + augment manifest, all patch-versioned under this root.
export const ARAM_MAYHEM_BASE_URL = "https://static.bigbrain.gg/custom-aram-mayhem";

// Augment ICONS live under a DIFFERENT root — the shared cdragon-custom
// bucket, not custom-aram-mayhem. Easy to conflate; keep them separate.
// (Note this is also not the `cdragon-custom/classic/<version>` prefix the
// Classic pipeline uses — unrelated pipelines sharing a bucket. Mind the
// ordering too: Mayhem is <patch>/augments, Classic is classic/<version>.)
export const AUGMENT_ICON_BASE_URL = "https://static.bigbrain.gg/cdragon-custom";

export function getAramMayhemAugmentIconUrl(patch: string, augmentId: number | string) {
  return `${AUGMENT_ICON_BASE_URL}/${patch}/augments/${augmentId}.webp`;
}

// Narrow set of rarities that participate in the by-rarity augment
// tier list. Deliberately narrower than the manifest's `rarity`
// field (which is string-typed to preserve values like kEventChoice
// and kBronze that the tier lists don't render) — this type
// constrains only the tier-list JSON shape, not the manifest.
export type AugmentRarity = "kPrismatic" | "kGold" | "kSilver";

// ─────────────────────────────────────────────────────────────────────
// JSON payload interfaces
// ─────────────────────────────────────────────────────────────────────

// `tiers` is Partial throughout this module (matching the Classic
// types): an empty tier is a legitimate shape, and an unguarded
// `tiers["S+"].map(...)` would crash the page. Every current payload
// happens to carry all six letters, so this is hardening rather than a
// bug fix — but the type should describe the contract, not today's data.
export interface AugmentAggregatedTierList {
  tiers: Partial<Record<TierNameValue, number[]>>; // integer augment IDs
  lastUpdated: string;
}

// Per-champion augments grouped by rarity. NOT a tier list: each rarity
// maps to a FLAT array of augment ids already ordered best-first by the
// pipeline, so the FE renders them in receipt order and never sorts.
//
// This is the shape the champion build page renders from. The two
// differences from the retired by-rarity tier list are both deliberate:
// no `tiers` nesting (there are no tier letters here, just a ranking),
// and it is per-champion rather than global.
//
// Because rarity is the grouping key, an augment's rarity is knowable
// from this payload alone — which is why the champion page no longer
// needs the global by-rarity feed as an id → rarity lookup table.
export interface AugmentRarityRankings {
  rarities: Record<AugmentRarity, number[]>;
  lastUpdated: string;
}

// Augment manifest — flat id → display-name dict. Value is the
// name string directly, not a wrapper object: the pipeline
// deliberately dropped `id` (redundant with the key),
// `augmentNameId` (unused by the FE), `rarity` (moved into the
// tier-list JSON), and `iconUrl` (constructed FE-side from the
// id) to minimize wire size on the SSR critical path. Build icon URLs
// with getAramMayhemAugmentIconUrl above — note they hang off
// AUGMENT_ICON_BASE_URL (cdragon-custom), not ARAM_MAYHEM_BASE_URL.
export type AugmentManifest = Record<string, string>;

// Records constrain to the 6 valid tier letters so `data.tiers.foo`
// would be a compile-time error; Partial because an absent tier is a
// legitimate payload shape (see AugmentAggregatedTierList above).
//
// Tier values are numeric champion KEYS (62, 145, ...) — NOT the
// internal SKIN strings ("MonkeyKing", "Kaisa"). data_tierlists-html.py
// resolves SKIN → key via champion_id_to_key-{patch}.json before
// serializing, so the JSON we receive is already in the same
// key format the site uses everywhere else.
export interface AramMayhemChampionTierList {
  tiers: Partial<Record<TierNameValue, number[]>>;
  lastUpdated: string;
}

// ─────────────────────────────────────────────────────────────────────
// Cleaners — normalize the raw fetch payload into consumer-friendly
// shape (rename `tiers` → `tierlist`, seed empty defaults for the
// loading state so consumers can dereference without null-checks).
// ─────────────────────────────────────────────────────────────────────

// cleanAramMayhemChampionTierList pulls fields off `data` instead of returning stubs.
// Optional-chaining handles the `data === null | undefined` case
// the fetcher might pass during loading; nullish-coalescing seeds
// defaults.
export function cleanAramMayhemChampionTierList(data: AramMayhemChampionTierList | null | undefined) {
  const tierlist = data?.tiers ?? {};
  const lastUpdated = data?.lastUpdated ?? "";
  return { tierlist, lastUpdated };
}

// cleanAramMayhemAugmentTierList — same shape as the champion clean
// helper (defaults to empty record + empty string during loading) but
// the tier values are number arrays, not string arrays. Lives next to
// its hook for the same reason the champion pair does: they're a
// fetch-and-shape unit.
export function cleanAramMayhemAugmentTierList(data: AugmentAggregatedTierList | null | undefined) {
  const tierlist = data?.tiers ?? {};
  const lastUpdated = data?.lastUpdated ?? "";
  return { tierlist, lastUpdated };
}

// cleanAramMayhemAugmentRarityRankings — peels off the top-level
// `rarities` object. Same loading-state fallback pattern as the other
// clean helpers, so consumers can index `rarities[key]` unguarded.
export function cleanAramMayhemAugmentRarityRankings(data: AugmentRarityRankings | null | undefined) {
  const rarities = data?.rarities ?? ({} as AugmentRarityRankings["rarities"]);
  const lastUpdated = data?.lastUpdated ?? "";
  return { rarities, lastUpdated };
}

// cleanAramMayhemAugmentManifest — simpler than the tier-list cleaners
// because the manifest is just a flat flat id→name-string dict (no `tiers` /
// `rarities` wrapper, no `lastUpdated`). The empty-object fallback
// keeps the consumer's `manifest[id]` lookup safe during the loading
// state without forcing every callsite to null-check the dict itself.
export function cleanAramMayhemAugmentManifest(data: AugmentManifest | null | undefined) {
  return data ?? ({} as AugmentManifest);
}

// ─────────────────────────────────────────────────────────────────────
// Fetch hooks — thin JSONFetcher wrappers around the CDN URLs. All
// hang off ARAM_MAYHEM_BASE_URL, produced by the
// ugg-replay-stats-aggregator pipeline (data_tierlists-html.py).
// ─────────────────────────────────────────────────────────────────────

export function useAramMayhemChampionTierList<K = ReturnType<typeof cleanAramMayhemChampionTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AramMayhemChampionTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const effectiveSkip = skip || !patch;

  const url = `${ARAM_MAYHEM_BASE_URL}/${patch}/tierlist-champions-${patch}.json`;
  return useJSONFetcher<AramMayhemChampionTierList, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanAramMayhemChampionTierList(json) as K),
    customCacheKey,
  });
}

// Fetches the aggregated (global, non-rarity-grouped) augment tier
// list — all augments compete in one EMR distribution regardless of
// rarity. Companion to the by-rarity hook below; either can be used
// independently. URL pattern mirrors the champion endpoint, swapping
// `tierlist-champions` for `tierlist-augments`.
export function useAramMayhemAugmentTierList<K = ReturnType<typeof cleanAramMayhemAugmentTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AugmentAggregatedTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const effectiveSkip = skip || !patch;

  const url = `${ARAM_MAYHEM_BASE_URL}/${patch}/tierlist-augments-${patch}.json`;
  return useJSONFetcher<AugmentAggregatedTierList, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanAramMayhemAugmentTierList(json) as K),
    customCacheKey,
  });
}

// Fetches the augment manifest — one per patch. Used by the augment
// tier-list tables to render real icons + names instead of placeholder
// content, and by the champion-page SEO blurb to resolve S+ augment
// ids to display names.
//
// Takes an explicit `patch` (see the PATCH CONVENTION note in the file
// header): this is called from the tier-list pages, the champion build
// page, and the SEO block, so it can't validate against any one
// tier-list page id. effectiveSkip short-circuits when patch is
// missing, so a malformed
// `.../undefined/aram-mayhem-augment-manifest-undefined.json` never
// hits the CDN.
export function useAramMayhemAugmentManifest<K = ReturnType<typeof cleanAramMayhemAugmentManifest>>(
  options: { patch: string | undefined } & JSONFetcherOptions<AugmentManifest, K>,
) {
  const { patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;

  const effectiveSkip = skip || !patch;

  const url = `${ARAM_MAYHEM_BASE_URL}/${patch}/aram-mayhem-augment-manifest-${patch}.json`;
  return useJSONFetcher<AugmentManifest, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanAramMayhemAugmentManifest(json) as K),
    customCacheKey,
  });
}

// Fetches the per-champion augment tier list — one champion's worth of
// augment data, in the same shape as the aggregated augment tier list.
// The underlying JSON is one file per champion under
// `tierlist-per-champion-augments-<patch>/` on the CDN.
//
// Takes an explicit `patch` (see the file header) because it's consumed
// on the champion-build page, not a tier-list route. The fetch is keyed
// by championKey AND patch, so both are named options. championKey is
// the numeric champion identifier (62 for MonkeyKing, 145 for Kaisa) —
// the filename stem the pipeline writes after resolving SKIN → key, so
// callsites pass the championId they already hold.
//
// Reuses cleanAramMayhemAugmentTierList: the payload shape is identical
// to the aggregated feed, so a second copy would just be drift waiting
// to happen. The hook name stays per-champion-specific so callsites are
// clear about which endpoint they're hitting.
export function useAramMayhemPerChampionAugmentTierList<K = ReturnType<typeof cleanAramMayhemAugmentTierList>>(
  options: { championKey: number; patch: string | undefined } & JSONFetcherOptions<AugmentAggregatedTierList, K>,
) {
  const { championKey, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;

  // Skip when either URL component is missing — keeps the JSONFetcher
  // cache key stable across renders (no flapping between a "loading"
  // state on a malformed URL and a real request once data arrives) and
  // avoids hitting the CDN with
  // ".../tierlist-augments-undefined-undefined.json" during SSR /
  // initial mount before context hydrates. championKey === 0 counts as
  // missing: Riot's key numbering starts at 1, so 0 means "unresolved".
  const effectiveSkip = skip || !patch || !championKey;

  const url = `${ARAM_MAYHEM_BASE_URL}/${patch}/tierlist-per-champion-augments-${patch}/tierlist-augments-${championKey}-${patch}.json`;
  return useJSONFetcher<AugmentAggregatedTierList, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanAramMayhemAugmentTierList(json) as K),
    customCacheKey,
  });
}

// Fetches one champion's augments grouped by rarity — the feed behind the
// ARAM Mayhem tab on the champion build page.
//
// Sibling directory to the per-champion tier-list feed above, and the same
// filename stem, so the two are easy to confuse on the CDN:
//   tierlist-per-champion-augments-<patch>/tierlist-augments-<key>-<patch>.json
//   tierlist-per-champion-augments-rarity-<patch>/tierlist-augments-<key>-<patch>.json
// Only the DIRECTORY differs. The `-rarity` one is this hook.
//
// Explicit `patch` and the same effectiveSkip guard as the tier-list feed:
// this is called from the champion build page, and skipping while either URL
// component is unresolved keeps a
// ".../tierlist-augments-undefined-undefined.json" off the CDN and the
// JSONFetcher cache key stable. championKey === 0 counts as missing — Riot's
// key numbering starts at 1, so 0 means "unresolved".
export function useAramMayhemPerChampionAugmentsByRarity<K = ReturnType<typeof cleanAramMayhemAugmentRarityRankings>>(
  options: { championKey: number; patch: string | undefined } & JSONFetcherOptions<AugmentRarityRankings, K>,
) {
  const { championKey, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;

  const effectiveSkip = skip || !patch || !championKey;

  const url = `${ARAM_MAYHEM_BASE_URL}/${patch}/tierlist-per-champion-augments-rarity-${patch}/tierlist-augments-${championKey}-${patch}.json`;
  return useJSONFetcher<AugmentRarityRankings, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanAramMayhemAugmentRarityRankings(json) as K),
    customCacheKey,
  });
}
